
import { defineComponent } from 'vue';
import BizIcon from '@/components/base/BizIcon.vue';
import { debounce } from '@/utils';

export interface SearchResult {
    estimatedTotalHits: number;
    hits: Record<string, any>;
    limit: number;
    offset: number;
    processingTimeMs: number;
    requestUid: number;
}

const SEARCH_BASE_URL = '/.rest/api/v1/search';
const SEARCH_ENDPOINT = 'multi-search';
const LIMIT = 10;
const punycode = require('punycode/');

export default defineComponent({
    components: { BizIcon },
    props: {
        placeholder: String,
        resultsLabel: String,
        indexLabels: Object,
        serviceLabel: String,
        noResultsMessage: String,
        indexes: Array,
        quickFilters: Array
    },
    data() {
        return {
            searchValue: '',
            preflightResult: null as SearchResult,
            result: null as SearchResult,
            results: [] as Record<string, any>[],
            preflightResults: [] as Record<string, any>[],
            endReached: false,
            offset: 0,
            filter: '',
            loading: false,
            error: false,
            onInput: debounce(() => this.onSearch(false), 1000)
        };
    },
    mounted() {
        const urlParams = new URLSearchParams(window.location.search);
        const query = urlParams.get('query');
        if (query) {
            this.searchValue = punycode.toUnicode(query);
            this.onSearch(false);
        }

        document.addEventListener('scroll', this.onScroll);
    },
    beforeUnmount() {
        document.removeEventListener('scroll', this.onScroll);
    },
    methods: {
        onScroll() {
            if (this.$el.getBoundingClientRect().bottom - window.innerHeight < 0) {
                if (!this.endReached) {
                    this.endReached = true;
                }
            } else {
                if (this.endReached) {
                    this.endReached = false;
                }
            }
        },
        async onSearch(append = false) {
            if (!append) {
                this.offset = 0;
            }

            this.loading = true;
            const sanitizedInput = punycode.toASCII(this.searchValue.trim());

            if (
                this.result &&
                this.result.query === sanitizedInput &&
                this.result.filter === this.filter
            ) {
                this.loading = false;
                return;
            }

            if (sanitizedInput.length >= 2) {
                if (history.pushState) {
                    const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?query=${sanitizedInput}`;
                    window.history.pushState({ path: newUrl }, '', newUrl);
                }
                try {
                    this.error = false;

                    // have to do a preflight request to fetch total result data per index (doesn't retrieve any actual contents)
                    const preflightReq = this.buildPreflightRequest(sanitizedInput);
                    const preflightResponse = await fetch(
                        `${this.$contextPath}${SEARCH_BASE_URL}/${this.$siteName}/${this.$lang}/${SEARCH_ENDPOINT}`,
                        {
                            method: 'POST',
                            body: JSON.stringify(preflightReq),
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        }
                    );

                    if (preflightResponse.status !== 200) {
                        this.reset();
                        this.loading = false;
                        return;
                    }

                    this.preflightResult = (await preflightResponse.json()) as SearchResult;
                    this.preflightResults = this.preflightResult.hits;

                    // main search request
                    const req = this.buildMeiliSearchRequest(sanitizedInput, LIMIT, this.offset);
                    const response = await fetch(
                        `${this.$contextPath}${SEARCH_BASE_URL}/${this.$siteName}/${this.$lang}/${SEARCH_ENDPOINT}`,
                        {
                            method: 'POST',
                            body: JSON.stringify(req),
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        }
                    );
                    if (response.status === 200) {
                        this.result = (await response.json()) as SearchResult;
                        if (append) {
                            this.results.push(...this.result.hits);
                        } else {
                            this.results = this.result.hits;
                        }
                    } else {
                        this.reset();
                    }
                } catch (e) {
                    this.error = true;
                    console.log(e);
                }
            } else {
                this.reset();
                this.error = false;
            }
            this.loading = false;
        },
        reset(): void {
            this.result = null;
            this.results = [];
            this.preflightResults = [];
        },
        toggleFilter(name: string) {
            this.filter = this.filter === name ? '' : name;
            this.onSearch();
        },
        removeHtmlTags(text: string) {
            return text
                .replace(/&amp;/g, '&')
                .replace(/&amp;shy;/g, '&')
                .replace(/&lt;br\s?\/?&gt;/g, ' ')
                .replace(/&shy;/g, '')
                .replace(/<br\s?\/?>/g, ' ');
        },
        buildPreflightRequest(q: string) {
            const localeWithoutSuffix =
                this.$lang.indexOf('_') >= 0 ? this.$lang.split('_')[0] : this.$lang;
            const req = {
                queries: [],
                federation: {
                    limit: 1000,
                    offset: 0
                }
            };
            this.indexes.forEach(index => {
                const query = {
                    indexUid: index,
                    q: q,
                    filter: `sites = ${this.$siteName}`,
                    attributesToRetrieve: [],
                    attributesToSearchOn: [`title_${this.$lang}`, `body_${this.$lang}`],
                    locales: [localeWithoutSuffix]
                };
                req.queries.push(query);
            });
            return req;
        },
        buildMeiliSearchRequest(q: string, limit: number, offset: number) {
            const localeWithoutSuffix =
                this.$lang.indexOf('_') >= 0 ? this.$lang.split('_')[0] : this.$lang;
            const req = {
                queries: [],
                federation: {
                    limit: limit,
                    offset: offset
                }
            };
            // filtered -> only one index; rest -> all indexes
            const indexes: string[] = this.filter ? [this.filter] : this.indexes;
            indexes.forEach(index => {
                const query = {
                    indexUid: index,
                    q: q,
                    filter: `sites = ${this.$siteName}`,
                    attributesToRetrieve: [
                        'workspace',
                        'id',
                        `kicker_${this.$lang}`,
                        `title_${this.$lang}`,
                        `body_${this.$lang}`,
                        'image',
                        'imageAlt'
                    ],
                    attributesToSearchOn: [`title_${this.$lang}`, `body_${this.$lang}`],
                    attributesToCrop: [`body_${this.$lang}`],
                    cropLength: 50,
                    locales: [localeWithoutSuffix]
                };
                req.queries.push(query);
            });
            return req;
        },
        getProperty(propertyName: string, result: Record<string, any>): string {
            if (result.hasOwnProperty(propertyName + '_' + this.$lang)) {
                return result[propertyName + '_' + this.$lang];
            } else if (result.hasOwnProperty(propertyName)) {
                return result[propertyName];
            }
            return undefined;
        },
        getFormattedProperty(propertyName: string, result: Record<string, any>): string {
            return this.getProperty(propertyName, result._formatted);
        },
        getNoOfResultsForFilter(filter: string): number {
            return this.preflightResults.filter(
                (r: Record<string, any>) => r._federation.indexUid === filter
            ).length;
        }
    },
    watch: {
        endReached() {
            if (this.endReached) {
                if (this.result && this.result.estimatedTotalHits > this.results.length) {
                    this.offset += LIMIT;
                    this.onSearch(true);
                }
            }
        }
    }
});
